import { cn } from '@goaltree/ui/lib/utils'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  getFrameAsk,
  getSelfCamera,
  registerSelfPreview,
  selfCameraStream,
  subscribeFrameAsk,
  subscribeSelfCamera,
} from '@/lib/self-camera'

const camOff = { on: false, error: null as null }

export function SelfCameraPreview({ className }: { className?: string }) {
  const cam = useSyncExternalStore(subscribeSelfCamera, getSelfCamera, () => camOff)
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !cam.on) return
    el.srcObject = selfCameraStream()
    void el.play().catch(() => {})
    return registerSelfPreview(el)
  }, [cam.on])
  if (!cam.on) return null
  return (
    <span
      className={cn(
        'relative block overflow-hidden rounded-full bg-black ring-2 ring-primary shadow-lg',
        className,
      )}
    >
      <video ref={ref} autoPlay muted playsInline className="size-full object-cover" />
      <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-red-500" />
    </span>
  )
}

// The coach grabbed a frame. Nothing is saved until they tap Use it.
export function CheckinFrameAsk() {
  const ask = useSyncExternalStore(subscribeFrameAsk, getFrameAsk, () => null)
  const { t } = useTranslation()
  useEffect(() => {
    if (!ask) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') ask.resolve(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ask])
  if (!ask || typeof document === 'undefined') return null
  const title = ask.title.split('///')[0].trim()
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/55 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkin-frame-title"
      onClick={() => ask.resolve(false)}
    >
      <div
        className="w-full max-w-sm rounded-3xl bg-background p-4 text-foreground shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={ask.url}
          alt={title}
          className={cn(
            'aspect-square w-full rounded-2xl object-cover',
            ask.styling && 'opacity-60',
          )}
        />
        {ask.effects.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-bold text-muted-foreground">{t('checkinFrame.looks')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {ask.effects.map((effect) => (
                <button
                  key={effect.id}
                  type="button"
                  disabled={ask.styling}
                  aria-pressed={ask.appliedId === effect.id}
                  onClick={() => ask.apply(effect)}
                  className={cn(
                    'h-9 cursor-pointer rounded-full px-3 text-sm font-bold disabled:cursor-default',
                    ask.appliedId === effect.id
                      ? 'bg-foreground text-background'
                      : 'bg-muted text-foreground',
                  )}
                >
                  {effect.label}
                </button>
              ))}
              {ask.appliedId && (
                <button
                  type="button"
                  disabled={ask.styling}
                  onClick={() => ask.revert()}
                  className="h-9 cursor-pointer rounded-full px-3 text-sm font-bold text-muted-foreground disabled:cursor-default"
                >
                  {t('checkinFrame.original')}
                </button>
              )}
            </div>
            {ask.styling && (
              <p className="mt-2 text-sm text-muted-foreground">{t('checkinFrame.styling')}</p>
            )}
            {ask.styleError && (
              <p className="mt-2 text-sm text-muted-foreground">{t('checkinFrame.styleFailed')}</p>
            )}
          </div>
        )}
        <p id="checkin-frame-title" className="mt-3 text-base font-extrabold">
          {t('checkinFrame.title')}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{t('checkinFrame.body', { title })}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => ask.resolve(false)}
            className="h-12 cursor-pointer rounded-2xl text-sm font-bold text-muted-foreground hover:bg-muted"
          >
            {t('checkinFrame.notNow')}
          </button>
          <button
            type="button"
            disabled={ask.styling}
            onClick={() => ask.resolve(true)}
            className="h-12 cursor-pointer rounded-2xl bg-foreground text-sm font-extrabold text-background disabled:opacity-60"
          >
            {t('checkinFrame.use')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
